import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { Clock, CheckCircle, AlertTriangle, MessageSquare, Send } from 'lucide-react';
import websocket from '../services/websocket';

const P2POrderPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chatMessage, setChatMessage] = useState('');
    const [messages, setMessages] = useState([]);
    const [sendingMessage, setSendingMessage] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        // Connect to WebSocket with auth token
        const token = localStorage.getItem('token');
        if (token) {
            websocket.connect(token);
        }

        fetchOrder();
        fetchMessages();
        const interval = setInterval(fetchOrder, 5000); // Poll for updates
        return () => clearInterval(interval);
    }, [id]);

    // WebSocket listener for new messages
    useEffect(() => {
        const handleNewMessage = (messageData) => {
            if (messageData.orderId === id) {
                setMessages(prev => {
                    // Avoid duplicates
                    if (prev.some(m => m.id === messageData.id)) return prev;
                    return [...prev, messageData];
                });
            }
        };

        // Subscribe to p2p_message events
        const unsubscribe = websocket.subscribe('p2p_message', handleNewMessage);
        return () => unsubscribe();
    }, [id]);

    // Auto scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const fetchOrder = async () => {
        try {
            const res = await api.get(`/p2p/orders/${id}`);
            setOrder(res.data.order);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch order:', error);
            toast.error('Failed to load order details');
            navigate('/p2p');
        }
    };

    const fetchMessages = async () => {
        try {
            const res = await api.get(`/p2p/orders/${id}/messages`);
            setMessages(res.data.messages || []);
        } catch (error) {
            console.error('Failed to fetch messages:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!chatMessage.trim() || sendingMessage) return;

        setSendingMessage(true);
        try {
            const res = await api.post(`/p2p/orders/${id}/messages`, { message: chatMessage });
            // Add message to state (WebSocket will also emit, but this is instant)
            setMessages(prev => {
                if (prev.some(m => m.id === res.data.message.id)) return prev;
                return [...prev, res.data.message];
            });
            setChatMessage('');
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleAction = async (action) => {
        if (!window.confirm(`Are you sure you want to ${action} this order?`)) return;

        try {
            await api.post(`/p2p/orders/${id}/${action}`);
            toast.success(`Order ${action}ed successfully`);
            fetchOrder();
        } catch (error) {
            toast.error(error.response?.data?.error || `Failed to ${action} order`);
        }
    };

    if (loading || !order) return <div className="min-h-screen bg-[#0b0e11] flex items-center justify-center text-white">Loading...</div>;

    const isBuyer = user.id === order.buyerId;
    const isSeller = user.id === order.sellerId;

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'text-green-500';
            case 'cancelled': return 'text-red-500';
            case 'paid': return 'text-yellow-500';
            default: return 'text-blue-500';
        }
    };

    return (
        <div className="min-h-screen bg-[#0b0e11] text-gray-100 font-sans">
            <Navbar />

            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Order Details */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Header Card */}
                        <div className="bg-[#181a20] rounded-3xl p-8 border border-gray-800">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <h1 className="text-2xl font-bold text-white">
                                            {isBuyer ? 'Buy' : 'Sell'} {order.P2PAd?.asset}
                                        </h1>
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold bg-gray-800 border border-gray-700 ${getStatusColor(order.status)}`}>
                                            {order.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <p className="text-gray-400 text-sm">Order ID: {order.id}</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm text-gray-400 mb-1">Total Amount</div>
                                    <div className="text-3xl font-bold text-white">
                                        {parseFloat(order.fiatAmount).toFixed(2)} <span className="text-lg text-gray-500">{order.P2PAd?.fiatCurrency}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-6 p-6 bg-[#0b0e11] rounded-2xl border border-gray-800">
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">Price</div>
                                    <div className="font-medium text-white">{parseFloat(order.price).toFixed(2)}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">Crypto Amount</div>
                                    <div className="font-medium text-white">{parseFloat(order.amount).toFixed(6)} {order.P2PAd?.asset}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">Payment Method</div>
                                    <div className="font-medium text-white">{order.paymentMethod}</div>
                                </div>
                            </div>
                        </div>

                        {/* Instructions / Payment Info */}
                        <div className="bg-[#181a20] rounded-3xl p-8 border border-gray-800">
                            <h3 className="text-xl font-bold text-white mb-6">
                                {order.status === 'created' ? 'Payment Pending' : 'Order Progress'}
                            </h3>

                            {order.status === 'created' && (
                                <div className="space-y-4">
                                    <div className="p-4 bg-blue-900/20 border border-blue-900/50 rounded-xl flex gap-3">
                                        <AlertTriangle className="text-blue-400 shrink-0" />
                                        <div className="text-sm text-blue-200">
                                            {isBuyer
                                                ? "Please transfer the exact amount to the seller's account below. Do not include any crypto-related remarks."
                                                : "Wait for the buyer to transfer funds. Do not release crypto until you confirm receipt in your bank account."
                                            }
                                        </div>
                                    </div>

                                    {isBuyer && (
                                        <div className="p-6 bg-[#0b0e11] rounded-xl border border-gray-800">
                                            <h4 className="font-bold text-white mb-4">Seller's Payment Details</h4>
                                            <div className="space-y-3 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-400">Method</span>
                                                    <span className="text-white">{order.paymentMethod}</span>
                                                </div>
                                                {/* Get seller's payment details for the selected method */}
                                                {(() => {
                                                    const sellerPayment = order.seller?.paymentMethods?.find(
                                                        pm => pm.method === order.paymentMethod
                                                    );
                                                    if (sellerPayment?.details) {
                                                        return (
                                                            <>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-400">Account Name</span>
                                                                    <span className="text-white">
                                                                        {sellerPayment.details.accountName || sellerPayment.details.name ||
                                                                            `${order.seller?.firstName || ''} ${order.seller?.lastName || ''}`.trim() || 'N/A'}
                                                                    </span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-400">
                                                                        {order.paymentMethod === 'UPI' ? 'UPI ID' :
                                                                            order.paymentMethod === 'Bank Transfer' ? 'Account Number' : 'Account'}
                                                                    </span>
                                                                    <span className="text-white font-mono">
                                                                        {sellerPayment.details.upiId ||
                                                                            sellerPayment.details.accountNumber ||
                                                                            sellerPayment.details.account || 'N/A'}
                                                                    </span>
                                                                </div>
                                                                {sellerPayment.details.ifscCode && (
                                                                    <div className="flex justify-between">
                                                                        <span className="text-gray-400">IFSC Code</span>
                                                                        <span className="text-white font-mono">{sellerPayment.details.ifscCode}</span>
                                                                    </div>
                                                                )}
                                                                {sellerPayment.details.bankName && (
                                                                    <div className="flex justify-between">
                                                                        <span className="text-gray-400">Bank Name</span>
                                                                        <span className="text-white">{sellerPayment.details.bankName}</span>
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    }
                                                    return (
                                                        <div className="text-yellow-400 text-xs">
                                                            Seller has not added payment details for this method. Please contact seller via chat.
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="mt-8 flex gap-4">
                                {isBuyer && order.status === 'created' && (
                                    <>
                                        <button
                                            onClick={() => handleAction('pay')}
                                            className="flex-1 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-blue-900/20"
                                        >
                                            Transferred, Notify Seller
                                        </button>
                                        <button
                                            onClick={() => handleAction('cancel')}
                                            className="px-6 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-bold transition-all"
                                        >
                                            Cancel Order
                                        </button>
                                    </>
                                )}

                                {isSeller && order.status === 'paid' && (
                                    <>
                                        <button
                                            onClick={() => handleAction('release')}
                                            className="flex-1 py-4 bg-green-600 hover:bg-green-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-green-900/20"
                                        >
                                            Payment Received, Release Crypto
                                        </button>
                                        <button
                                            className="px-6 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-bold transition-all"
                                        >
                                            Appeal
                                        </button>
                                    </>
                                )}

                                {order.status === 'completed' && (
                                    <div className="w-full p-4 bg-green-900/20 border border-green-900/50 rounded-xl flex items-center justify-center gap-2 text-green-400 font-bold">
                                        <CheckCircle /> Order Completed
                                    </div>
                                )}
                                {order.status === 'cancelled' && (
                                    <div className="w-full p-4 bg-red-900/20 border border-red-900/50 rounded-xl flex items-center justify-center gap-2 text-red-400 font-bold">
                                        <AlertTriangle /> Order Cancelled
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Chat Section */}
                    <div className="lg:col-span-1">
                        <div className="bg-[#181a20] rounded-3xl border border-gray-800 h-[600px] flex flex-col">
                            <div className="p-6 border-b border-gray-800">
                                <h3 className="font-bold text-white flex items-center gap-2">
                                    <MessageSquare size={20} className="text-blue-500" />
                                    Chat with {isBuyer ? 'Seller' : 'Buyer'}
                                </h3>
                            </div>

                            <div className="flex-1 p-4 overflow-y-auto space-y-3">
                                {messages.length === 0 ? (
                                    <div className="text-center text-gray-500 mt-8">
                                        <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No messages yet. Start the conversation!</p>
                                    </div>
                                ) : (
                                    messages.map((msg) => {
                                        const isMe = msg.senderId === user.id;
                                        return (
                                            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`rounded-2xl px-4 py-3 max-w-[80%] ${isMe
                                                    ? 'bg-blue-600 rounded-tr-none text-white'
                                                    : 'bg-gray-800 rounded-tl-none text-gray-300'}`}>
                                                    {!isMe && (
                                                        <p className="text-xs text-blue-400 mb-1">
                                                            {msg.sender?.firstName || 'User'}
                                                        </p>
                                                    )}
                                                    <p className="text-sm">{msg.message}</p>
                                                    <span className={`text-[10px] mt-1 block ${isMe ? 'text-blue-200' : 'text-gray-500'}`}>
                                                        {formatTime(msg.createdAt)}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            <div className="p-4 border-t border-gray-800">
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={chatMessage}
                                        onChange={(e) => setChatMessage(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                        placeholder="Type a message..."
                                        className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl pl-4 pr-12 py-3 text-white focus:outline-none focus:border-blue-500"
                                        disabled={sendingMessage}
                                    />
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={sendingMessage || !chatMessage.trim()}
                                        className="absolute right-2 top-2 p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Send size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default P2POrderPage;
